import { parseCostApiKeys } from "@repo/shared";
import { CostTypeCategory } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getCostApiKeyLabel } from "@/lib/constants/cost-api-keys";
import { useDeleteCostType } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { AdvisorOverrideBadge } from "./advisor-override-badge";
import { CostTypeDialog } from "./cost-type-dialog";
import { T } from "./cost-types-table.i18n";

type CostTypesTableProps = {
  portfolioCollectionId: string;
  costTypes: any[];
};

export function CostTypesTable({ portfolioCollectionId, costTypes }: CostTypesTableProps) {
  const localize = useLocalize();
  const t = useT(T);

  const deleteCostType = useDeleteCostType();
  const isPending = deleteCostType.isPending;

  const CATEGORY_LABELS: Record<CostTypeCategory, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
    WITHDRAWAL: t.categoryWithdrawal,
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCostType, setEditingCostType] = useState<any | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [costTypeToDelete, setCostTypeToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleEdit = (costType: any) => {
    setEditingCostType(costType);
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (!costTypeToDelete) return;

    deleteCostType.mutate(
      { collectionId: portfolioCollectionId, ctId: costTypeToDelete },
      {
        onSuccess: () => toast.success(t.costTypeDeleted),
        onError: (err) => showAlert(t.error, err.message || t.failedToDelete, "destructive"),
      },
    );
    setCostTypeToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingCostType(null);
  };

  const formatApiKeys = (apiKeysJson: string): string => {
    const keys = parseCostApiKeys(apiKeysJson);
    if (keys.length === 0) return t.noKeysConfigured;
    if (keys.length <= 2) {
      return keys.map((k) => getCostApiKeyLabel(k)).join(", ");
    }
    return `${getCostApiKeyLabel(keys[0])}, ${getCostApiKeyLabel(keys[1])} +${keys.length - 2} ${t.more}`;
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={() => setDialogOpen(true)}>{t.addCostType}</Button>
        </div>

        {costTypes.length === 0 ? (
          <EmptyState>{t.noCostTypes}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.category}</TableHead>
                <TableHead>{t.mappedApiKeys}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {costTypes.map((costType: any) => (
                <TableRow key={costType.id}>
                  <TableCell className="font-medium">
                    {localize(costType.nameI18n)}
                    <AdvisorOverrideBadge mode={costType.advisorOverride} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {CATEGORY_LABELS[costType.category as CostTypeCategory]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatApiKeys(costType.apiKeys)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => handleEdit(costType)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => {
                          setCostTypeToDelete(costType.id);
                          setConfirmDeleteOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CostTypeDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        portfolioCollectionId={portfolioCollectionId}
        costType={editingCostType}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteCostType}
        description={t.deleteConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
